import cn from 'classnames';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/shared/ui/core';
import { forwardRef, ReactNode, TextareaHTMLAttributes } from 'react';

import s from './textarea.module.scss';


type TextareaState = 'default' | 'success' | 'error';


export interface TextareaProps
    extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
    description?: ReactNode;
    error?: ReactNode;
    label?: ReactNode;
    required?: boolean;
    state?: TextareaState;
    textareaClassName?: string;
}


export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea ({
    className,
    textareaClassName,
    label,
    description,
    error,
    state = 'default',
    disabled,
    required,
    ...props
}, ref) {
    const isError   = state === 'error' || Boolean(error);
    const isSuccess = state === 'success';

    const stateClassName = cn({
        [s.destructive]: isError,
        [s.success]    : isSuccess,
        [s.disabled]   : disabled,
    });

    return (
        <Field className={ className }>
            <div
                className={ cn(
                    s.root,
                    stateClassName,
                ) }
            >
                {
                    label &&
                    <FieldLabel
                        className={ stateClassName }
                        required={ required }
                    >
                        { label }
                    </FieldLabel>
                }

                <textarea
                    ref={ ref }
                    disabled={ disabled }
                    className={ cn(
                        s.textarea,
                        stateClassName,
                        textareaClassName,
                    ) }
                    { ...props }
                />
            </div>

            {
                isError &&
                error &&
                <FieldError className={ stateClassName }>
                    { error }
                </FieldError>
            }

            {
                !isError &&
                description &&
                <FieldDescription className={ stateClassName }>
                    { description }
                </FieldDescription>
            }
        </Field>
    );
});
